import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../components/common';
import { getErrorMessage } from '../../api/client';

export function CatalogSetupPage() {
  return (
    <div>
      <PageHeader title="Catalog setup" subtitle="Manage product categories and brands" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
        <ReferenceManager
          title="Categories"
          queryKey="categories"
          list={() => inventoryApi.listCategories()}
          create={(name) => inventoryApi.createCategory({ name })}
          update={(id, name) => inventoryApi.updateCategory(id, { name })}
          remove={(id) => inventoryApi.deleteCategory(id)}
          noun="category"
        />
        <ReferenceManager
          title="Brands"
          queryKey="brands"
          list={() => inventoryApi.listBrands()}
          create={(name) => inventoryApi.createBrand({ name })}
          update={(id, name) => inventoryApi.updateBrand(id, { name })}
          remove={(id) => inventoryApi.deleteBrand(id)}
          noun="brand"
        />
      </div>
    </div>
  );
}

interface RefItem { id: string; name: string; status?: string; }

function ReferenceManager(props: {
  title: string; queryKey: string; noun: string;
  list: () => Promise<RefItem[]>;
  create: (name: string) => Promise<any>;
  update: (id: string, name: string) => Promise<any>;
  remove: (id: string) => Promise<any>;
}) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const q = useQuery({ queryKey: [props.queryKey], queryFn: props.list });

  const invalidate = () => qc.invalidateQueries({ queryKey: [props.queryKey] });
  const createMut = useMutation({
    mutationFn: () => props.create(newName.trim()),
    onSuccess: () => { toast.success(`${cap(props.noun)} created`); setNewName(''); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: () => props.update(editId!, editName.trim()),
    onSuccess: () => { toast.success(`${cap(props.noun)} updated`); setEditId(null); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => props.remove(id),
    onSuccess: (r: any) => { toast.success(r?.message || `${cap(props.noun)} deleted`); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const items = q.data ?? [];

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 18 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', marginBottom: 12 }}>{props.title}</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) createMut.mutate(); }}
          placeholder={`New ${props.noun} name`}
          style={{ flex: 1, padding: '8px 11px', borderRadius: 9, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none' }}
        />
        <button
          onClick={() => createMut.mutate()}
          disabled={!newName.trim() || createMut.isPending}
          style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: '#0d9488', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: (!newName.trim() || createMut.isPending) ? 0.6 : 1 }}>
          Add
        </button>
      </div>

      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> :
        items.length === 0 ? <EmptyState message={`No ${props.noun} yet`} /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((it) => (
            <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, background: '#f8fafc' }}>
              {editId === it.id ? (
                <>
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter' && editName.trim()) updateMut.mutate(); if (e.key === 'Escape') setEditId(null); }}
                    style={{ flex: 1, padding: '5px 9px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14 }} />
                  <button onClick={() => updateMut.mutate()} disabled={updateMut.isPending}
                    style={{ fontSize: 12, fontWeight: 700, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer' }}>Save</button>
                  <button onClick={() => setEditId(null)}
                    style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: 14, color: '#0f172a' }}>{it.name}</span>
                  {it.status && it.status !== 'active' && <StatusBadge status={it.status} />}
                  <button onClick={() => { setEditId(it.id); setEditName(it.name); }}
                    style={{ fontSize: 12, fontWeight: 600, color: '#475569', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>Edit</button>
                  <button
                    onClick={() => { if (window.confirm(`Delete ${props.noun} "${it.name}"? If it is in use it will be deactivated instead.`)) deleteMut.mutate(it.id); }}
                    disabled={deleteMut.isPending}
                    style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>Delete</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
